// Message lookup and formatting for the site's translations. The resources
// are i18next JSON v4 (https://www.i18next.com/misc/json-format), so the
// i18next library itself, or any translation platform that reads that
// format (Crowdin, Lokalise, Phrase, Weblate), takes them as they are. This
// module implements that format's documented behaviour on the browser's own
// Intl, with no dependency, so the same code runs in the build, in the
// pages' scripts and in the viewer:
//
//   keys       nested objects, addressed with dots: t('hero.title'); another
//              namespace with a colon: t('common:footer.search')
//   plurals    with a numeric `count`, key_zero (for 0, if present), then
//              key_<CLDR category> from Intl.PluralRules (one, few, many...),
//              then key_other
//   values     {{name}} escaped for HTML, {{- name}} as given, {{a.b}} a path
//   formats    {{n, number}}, {{n, number(maximumFractionDigits: 1)}},
//              {{d, datetime(month: long; day: numeric)}}, {{xs, list}},
//              {{n, currency(USD)}}, {{n, relativetime(day)}}
//   fallback   a key missing in the language comes from the fallback
//              language (English), and onMissing hears about it
//   objects    returnObjects: true gives an object or array of messages,
//              each formatted
//
// The messages are trusted (they are files in this repository) and may hold
// inline HTML; only the values put into them are escaped.

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '/': '&#x2F;' };
export const escapeHtml = (s) => String(s).replace(/[&<>"'/]/g, (c) => ESCAPES[c]);

const formatterCache = new Map();
function cached(kind, locale, options, make) {
  const key = `${kind}|${locale}|${JSON.stringify(options)}`;
  if (!formatterCache.has(key)) formatterCache.set(key, make());
  return formatterCache.get(key);
}

// "minimumFractionDigits: 2; maximumFractionDigits: 2" -> options. Numbers
// and booleans become themselves; anything else stays a string.
function parseOptions(text) {
  const out = {};
  if (!text) return out;
  for (const part of text.split(';')) {
    const i = part.indexOf(':');
    if (i < 0) continue;
    const k = part.slice(0, i).trim();
    const raw = part.slice(i + 1).trim();
    out[k] = raw === 'true' ? true : raw === 'false' ? false : raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : raw;
  }
  return out;
}

function applyFormat(value, spec, locale) {
  const m = /^\s*([a-z]+)\s*(?:\((.*)\))?\s*$/i.exec(spec);
  if (!m) return value;
  const name = m[1].toLowerCase();
  const arg = m[2] || '';
  switch (name) {
    case 'number': {
      const o = parseOptions(arg);
      return cached('n', locale, o, () => new Intl.NumberFormat(locale, o)).format(value);
    }
    case 'currency': {
      // currency(USD) or currency(currency: USD; minimumFractionDigits: 0)
      const o = { style: 'currency', ...(arg.includes(':') ? parseOptions(arg) : { currency: arg.trim() }) };
      return cached('c', locale, o, () => new Intl.NumberFormat(locale, o)).format(value);
    }
    case 'datetime': {
      const o = parseOptions(arg);
      return cached('d', locale, o, () => new Intl.DateTimeFormat(locale, o)).format(value instanceof Date ? value : new Date(value));
    }
    case 'relativetime': {
      // relativetime(day) or relativetime(range: day; style: short)
      const { range = 'day', ...o } = arg.includes(':') ? parseOptions(arg) : { range: arg.trim() || 'day' };
      return cached('r', locale, o, () => new Intl.RelativeTimeFormat(locale, o)).format(value, range);
    }
    case 'list': {
      const o = parseOptions(arg);
      return cached('l', locale, o, () => new Intl.ListFormat(locale, o)).format(value);
    }
    default:
      return value;
  }
}

const getPath = (obj, path) => path.split('.').reduce((o, k) => (o != null && typeof o === 'object' ? o[k] : undefined), obj);

// resources: { [locale]: { [namespace]: object } }.
// namespaces: the ones this translator reads, the first being the default.
export function createTranslator({ locale, resources, namespaces, fallbackLocale = 'en', escape = true, onMissing = null }) {
  const nss = namespaces && namespaces.length ? namespaces : Object.keys(resources[locale] || resources[fallbackLocale] || {});
  const plural = new Intl.PluralRules(locale);

  function lookup(lng, ns, key) {
    const bundle = resources[lng] && resources[lng][ns];
    return bundle ? getPath(bundle, key) : undefined;
  }

  function find(ns, key, count) {
    const candidates = [];
    if (typeof count === 'number') {
      if (count === 0) candidates.push(`${key}_zero`);
      candidates.push(`${key}_${plural.select(count)}`, `${key}_other`);
    }
    candidates.push(key);
    for (const lng of locale === fallbackLocale ? [locale] : [locale, fallbackLocale]) {
      for (const k of candidates) {
        const hit = lookup(lng, ns, k);
        if (hit !== undefined) {
          if (lng !== locale && onMissing) onMissing({ locale, ns, key });
          return hit;
        }
      }
    }
    if (onMissing) onMissing({ locale, ns, key, absent: true });
    return undefined;
  }

  function interpolate(message, values) {
    return message.replace(/\{\{(-?)\s*([^}]+?)\s*\}\}/g, (whole, raw, body) => {
      const comma = body.indexOf(',');
      const name = (comma < 0 ? body : body.slice(0, comma)).trim();
      let value = getPath(values, name);
      if (value === undefined || value === null) {
        if (onMissing) onMissing({ locale, key: name, interpolation: true });
        return '';
      }
      if (comma >= 0) value = applyFormat(value, body.slice(comma + 1), locale);
      return raw || !escape ? String(value) : escapeHtml(value);
    });
  }

  function deep(node, values) {
    if (typeof node === 'string') return interpolate(node, values);
    if (Array.isArray(node)) return node.map((n) => deep(n, values));
    if (node && typeof node === 'object') return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, deep(v, values)]));
    return node;
  }

  function t(fullKey, options = {}) {
    const i = fullKey.indexOf(':');
    const [ns, key] = i > 0 ? [fullKey.slice(0, i), fullKey.slice(i + 1)] : [nss[0], fullKey];
    const found = find(ns, key, options.count);
    if (found === undefined) return fullKey;
    if (typeof found !== 'string') {
      if (options.returnObjects) return deep(found, options);
      if (onMissing) onMissing({ locale, ns, key, notString: true });
      return fullKey;
    }
    return interpolate(found, options);
  }

  t.locale = locale;
  t.exists = (fullKey) => {
    const i = fullKey.indexOf(':');
    const [ns, key] = i > 0 ? [fullKey.slice(0, i), fullKey.slice(i + 1)] : [nss[0], fullKey];
    return lookup(locale, ns, key) !== undefined;
  };
  return t;
}
